<template>
  <div
    class="box is-paddingless chat"
    :data-tour="tourAnchor || null"
    :class="{ 'increase-chat-size': viewParticipants }"
  >
    <div v-if="viewParticipants && isActive" class="panel participants-panel">
      <div class="participants-content" :class="{ 'split-columns': mobile }">
        <div
          class="participant-content"
          v-for="participant in participants"
          :key="participant.connection"
        >
          <b-icon
            class="participant-icon"
            icon="circle-medium"
            :type="{ 'is-danger': !participant.online, 'is-success': participant.online }"
          ></b-icon>
          <span class="participant-alias" v-for="alias in participant.alias" :key="alias">{{
            participant.type.includes('seller') && participant.socialDenomination
              ? `(${alias}) ${reduceSocialDenomination(participant.socialDenomination)}`
              : alias
          }}</span>
          <button
            v-if="canBlockBidders && participant.type.includes('seller')"
            type="button"
            class="participant-block-control"
            :class="{ 'is-blocked': isBidderBlocked(participant.id[0]) }"
            :aria-label="
              isBidderBlocked(participant.id[0])
                ? 'Desbloquear licitante para envio de mensagens'
                : 'Bloquear licitante para envio de mensagens'
            "
            :title="
              isBidderBlocked(participant.id[0])
                ? 'Desbloquear licitante para envio de mensagens'
                : 'Bloquear licitante para envio de mensagens'
            "
            @click.stop.prevent="toggleBidderBlock(participant)"
          >
            <b-icon
              :icon="
                isBidderBlocked(participant.id[0]) ? 'comment-remove-outline' : 'comment-outline'
              "
              size="is-small"
            ></b-icon>
          </button>
        </div>
      </div>
    </div>

    <div class="panel chat-panel">
      <div
        class="panel-heading chat-header is-relative"
        :class="{ 'remove-border-chat-header': viewParticipants }"
        @click.stop="toggleChat"
      >
        <b-tag v-if="!isActive && quantity" class="chat-counter" type="is-danger">{{
          quantity
        }}</b-tag>

        <div class="header-actions">
          <template v-if="displayHeaderElements && isActive">
            <b-button
              size="is-small"
              type="is-primary"
              :icon-left="mobile ? 'arrow-expand-vertical' : 'arrow-expand-horizontal'"
              :data-tour="canManageChatPermissions ? 'aguardando-inicio-chat-participantes' : null"
              @click.stop.prevent="viewParticipants = !viewParticipants"
            >
              Participantes
            </b-button>

            <b-button
              v-if="displayAppealIntentionButton"
              size="is-small"
              type="is-primary"
              icon-left="gavel"
              :loading="manifestAppealLoading"
              :disabled="disableAppealIntentionButton"
              :data-tour="situation === 20 ? 'manifestacao-intencao-botao' : null"
              @click.stop.prevent="manifestAppealIntention"
            >
              {{ appealIntentionButtonText }}
            </b-button>
          </template>
        </div>

        <span class="chat-title"> Chat </span>

        <div class="chat-header-right">
          <b-tooltip
            v-if="displayHeaderElements"
            class="chat-status"
            :type="connected ? 'is-success' : 'is-danger'"
            :label="connected ? 'Conectado' : 'Desconectado'"
          >
            <b-icon
              icon="circle"
              size="is-small"
              :type="{ 'is-danger': !connected, 'is-success': connected }"
            ></b-icon>
          </b-tooltip>
        </div>
      </div>

      <div class="chat-content" ref="chat">
        <div
          v-if="canManageChatPermissions"
          data-tour="aguardando-inicio-chat-modo"
          class="chat-mode-toolbar"
        >
          <span class="chat-mode-toolbar-label">Modo do chat:</span>
          <div class="chat-mode-segmented">
            <button
              v-for="option in CHAT_MODE_OPTIONS"
              :key="option.value"
              type="button"
              class="chat-mode-segment"
              :class="{ 'is-active': chatMode === option.value }"
              :disabled="changingChatMode"
              @click.stop.prevent="changeChatModeTo(option.value)"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
        <div ref="messages" class="messages" @scroll="event => handleScroll(event.target)">
          <div class="panel-block notificacao">
            <div class="media-content">
              <a
                class="content has-text-centered load-messages"
                @click.stop.prevent="loadMoreMessages()"
              >
                <small class="tag">Carregar mensagens antigas</small>
              </a>
            </div>
          </div>
          <div
            :class="['panel-block', message.tipo === 1 ? 'notificacao' : 'conteudo']"
            v-for="message in messagesToShow"
            :key="message.id"
          >
            <div class="media-content">
              <div class="content" :class="{ 'has-text-centered': message.tipo === 1 }">
                <div v-if="message.tipo === 1">
                  <small class="tag">{{ message.mensagem }}</small>
                </div>

                <div v-else>
                  <p>
                    <strong :class="{ 'has-text-danger': message.tipo === 'SISTEMA' }">
                      {{ message.usuario }}
                    </strong>

                    <small class="is-pulled-right">{{
                      message.enviado | formatarDataSegundo
                    }}</small>
                  </p>
                </div>

                <div v-if="message.tipo !== 1">
                  <span>{{ message.mensagem }}</span>
                </div>
              </div>
            </div>
          </div>

          <a
            v-if="isActive && !scrollBottom && quantity"
            class="button is-small down-button"
            size="is-small"
            :class="{ 'down-button-without-message': !displayFieldButton }"
            @click.stop.prevent="goDownChat()"
          >
            <strong>Novas Mensagens</strong>
            <b-icon class="down-button-icon" icon="chevron-double-down" size="is-medium"></b-icon>
          </a>
        </div>
        <div v-if="displayFieldButton" class="insert-message">
          <form>
            <b-field @keyup.enter="sendMessage" expanded>
              <b-input
                expanded
                class="custom-chat-input"
                ref="inputMessage"
                placeholder="Digite uma mensagem"
                v-model="message"
                @input.native="event => firstLetterUpperCase(event.target.value)"
              ></b-input>

              <p class="control">
                <button
                  class="button is-primary custom-chat-button"
                  :class="{ 'is-loading': loading }"
                  @click.prevent="sendMessage"
                >
                  Enviar
                </button>
              </p>
            </b-field>
          </form>
        </div>
        <div v-else-if="displayChatRestrictedBanner" class="chat-restricted-banner">
          <b-icon icon="comment-remove-outline" size="is-small"></b-icon>
          <span v-if="bidderIsBlocked"
            >Você foi bloqueado(a) pelo Pregoeiro(a) para envio de mensagens nesta sessão.</span
          >
          <span v-else
            >O envio de mensagens está permitido apenas aos licitantes autorizados nesta etapa da
            sessão.</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import { funcoes, SituacaoEnum } from '@/utils/enumerados';
import { podeExibirBotaoIntencaoRecurso } from '@/utils/intencaoRecursoVisibility';

import { formatarData, formatarDataSegundo } from '@/utils/format';

import SocketResource from '@/resources/socket';

const MESSAGES_PER_LOAD = 20;

const HEADER_VISIBLE_SITUATIONS = new Set([
  SituacaoEnum.AGUARDANDO_INICIO_SESSAO,
  SituacaoEnum.ANALISANDO_PROPOSTAS,
  SituacaoEnum.LANCES,
  SituacaoEnum.PREFERENCIA,
  SituacaoEnum.NEGOCIACAO,
  SituacaoEnum.HABILITACAO,
  SituacaoEnum.INTERPOSICAO_DE_RECURSOS,
  SituacaoEnum.ADESAO_DE_PRECO,
  SituacaoEnum.JULGAMENTO,
  SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO,
]);

const FIELD_BUTTON_VISIBLE_SITUATIONS = new Set([
  SituacaoEnum.ANALISANDO_PROPOSTAS,
  SituacaoEnum.LANCES,
  SituacaoEnum.PREFERENCIA,
  SituacaoEnum.NEGOCIACAO,
  SituacaoEnum.HABILITACAO,
  SituacaoEnum.INTERPOSICAO_DE_RECURSOS,
  SituacaoEnum.ADESAO_DE_PRECO,
  SituacaoEnum.JULGAMENTO,
  SituacaoEnum.MANIFESTACAO_INTENCAO_RECURSO,
]);

const CHAT_MODE_OPTIONS = [
  { value: 'TODOS', label: 'Todos' },
  { value: 'MELHOR_OFERTA', label: 'Somente melhor oferta' },
  { value: 'NENHUM', label: 'Nenhum' },
];

// Empresa compradora side, for the one exception where they can message before the session
// even opens (see canMessageBeforeSessionStarts). Note funcoes['AGENTE-CONTRATACAO'] is kept
// here for symmetry with canManageChatPermissions below, but in practice `getFunction`
// already collapses a pure AGENTE-CONTRATACAO responsible into funcoes.PREGOEIRO before it
// reaches this component — this value is unreachable via `this.function`.
const BUYER_COMPANY_ROLES = [
  funcoes.PREGOEIRO,
  funcoes.PREGOEIRO_SUBSTITUTO,
  funcoes['AGENTE-CONTRATACAO'],
  funcoes['AGENTE-CONTRATACAO-SUBSTITUTO'],
  funcoes.AUTORIDADE,
];

// Local echo label only (shown to the sender before the real broadcast arrives) — the server
// always stamps the message with the sender's own alias, this never reaches other clients.
const NON_BIDDER_SENDER_LABELS = {
  [funcoes.PREGOEIRO]: 'Pregoeiro(a)',
  [funcoes.PREGOEIRO_SUBSTITUTO]: 'Pregoeiro(a) Substituto(a)',
  [funcoes['AGENTE-CONTRATACAO-SUBSTITUTO']]: 'Agente de Contratação Substituto(a)',
  [funcoes.AUTORIDADE]: 'Autoridade Competente',
};

export default {
  name: 'AuctionChat',
  props: {
    /** Âncora de feature tour; só deve existir na situação correspondente. */
    tourAnchor: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      message: '',
      quantity: 0,
      total: MESSAGES_PER_LOAD,
      isActive: false,
      loading: false,
      manifestAppealLoading: false,
      socketResource: new SocketResource(this.$socket),
      scrollBottom: false,
      viewParticipants: false,
      changingChatMode: false,
      CHAT_MODE_OPTIONS,
    };
  },
  watch: {
    situation(newSituation, oldSituation) {
      if (this.situation > 11) {
        this.viewParticipants = false;
      }
      this.fetchAppealIntention(oldSituation);
    },
    'auction.id': function watchAuctionId(auctionId) {
      this.fetchAppealIntention();
      this.fetchPermissions(auctionId);
    },
  },
  computed: {
    ...mapState('app', ['windowWidth']),
    ...mapState('auctionsBidders', ['userLoggedBidder']),
    ...mapGetters({
      auction: 'obterOfertaCompra',
      function: 'getFunction',
      hasAppealIntention: 'verificarOfertaCompraIntencaoRecursoManifestada',
      prazoManifestacaoIntencao: 'obterPrazoManifestacaoIntencao',
    }),
    ...mapState('chat', [
      'messages',
      'participants',
      'mode',
      'windowActive',
      'blockedBidderIds',
      'authorizedBidderIds',
    ]),
    messagesToShow() {
      return this.messages.slice(this.total * -1);
    },
    connected() {
      return this.$socket.connected;
    },
    chat() {
      return this.$refs.messages;
    },
    situation() {
      return this.auction.situacao.id;
    },
    authority() {
      return this.function === funcoes.AUTORIDADE;
    },
    crier() {
      return this.function === funcoes.PREGOEIRO;
    },
    visitor() {
      return this.function === funcoes.VISITANTE;
    },
    bidder() {
      return this.function === funcoes.LICITANTE;
    },
    chatMode() {
      return this.mode;
    },
    canManageChatPermissions() {
      // Also available in Aguardando Início da Sessão, so Pregoeiro/Agente de Contratação
      // can pre-configure mode/block before the session opens.
      if (
        !FIELD_BUTTON_VISIBLE_SITUATIONS.has(this.situation) &&
        this.situation !== SituacaoEnum.AGUARDANDO_INICIO_SESSAO
      ) {
        return false;
      }
      return (
        this.function === funcoes.PREGOEIRO ||
        this.function === funcoes.PREGOEIRO_SUBSTITUTO ||
        this.function === funcoes['AGENTE-CONTRATACAO'] ||
        this.function === funcoes['AGENTE-CONTRATACAO-SUBSTITUTO']
      );
    },
    // Aguardando Início da Sessão is the one stage where "who can message" is broader than
    // the crier-only rule below: the whole empresa compradora side, not just the Pregoeiro.
    // Every other stage's behavior is untouched.
    canMessageBeforeSessionStarts() {
      return (
        this.situation === SituacaoEnum.AGUARDANDO_INICIO_SESSAO &&
        BUYER_COMPANY_ROLES.includes(this.function)
      );
    },
    canBlockBidders() {
      return this.canManageChatPermissions && this.chatMode !== 'NENHUM';
    },
    bidderIsBlocked() {
      return !!this.userLoggedBidder && this.blockedBidderIds.includes(this.userLoggedBidder.id);
    },
    bidderCanSendUnderMode() {
      if (this.bidderIsBlocked) {
        return false;
      }
      if (this.chatMode === 'NENHUM') {
        return false;
      }
      if (this.chatMode === 'TODOS') {
        return true;
      }
      // MELHOR_OFERTA: outside the window it behaves exactly like Todos.
      if (!this.windowActive) {
        return true;
      }
      return !!this.userLoggedBidder && this.authorizedBidderIds.includes(this.userLoggedBidder.id);
    },
    displayFieldButton() {
      if (this.canMessageBeforeSessionStarts) {
        return true;
      }
      const baseVisible =
        (this.crier || this.bidder) && FIELD_BUTTON_VISIBLE_SITUATIONS.has(this.situation);
      if (!baseVisible) {
        return false;
      }
      if (this.crier) {
        return true;
      }
      return this.bidderCanSendUnderMode;
    },
    displayChatRestrictedBanner() {
      return (
        this.bidder &&
        FIELD_BUTTON_VISIBLE_SITUATIONS.has(this.situation) &&
        !this.bidderCanSendUnderMode
      );
    },
    displayHeaderElements() {
      return HEADER_VISIBLE_SITUATIONS.has(this.situation);
    },
    displayAppealIntentionButton() {
      return podeExibirBotaoIntencaoRecurso({
        auction: this.auction,
        situacao: this.situation,
        prazoManifestacao: this.prazoManifestacaoIntencao,
        isBidder: this.bidder,
      });
    },
    appealIntentionButtonText() {
      return this.hasAppealIntention
        ? 'Intenção de recurso registrada'
        : 'Manifestar intenção de recurso';
    },
    disableAppealIntentionButton() {
      return this.manifestAppealLoading || this.hasAppealIntention;
    },
    mobile() {
      return this.windowWidth <= 800;
    },
  },
  filters: {
    formatarData,
    formatarDataSegundo,
  },
  sockets: {
    'chat:notification': function chatNotification(message) {
      const data = {
        id: `N${this.messages.length + 1}`,
        tipo: 1,
        enviado: new Date(),
        mensagem: message,
      };
      this.addNewMessage(data);
    },
    'chat:connected': function chatConnected(message) {
      this.updateParticipantStatus(message);
    },
    'chat:disconnected': function chatDisconnected(message) {
      this.updateParticipantStatus(message);
    },
    'chat:message': function chatMessage(data) {
      this.addNewMessage(data);
    },
    'chat:update': function chatUpdate(data) {
      this.updateMessage(data);
    },
    'chat:permission-updated': function chatPermissionUpdated(payload) {
      this.updatePermissions(payload);
    },
  },
  methods: {
    ...mapActions('chat', [
      'addMessage',
      'updateMessage',
      'updateParticipantStatus',
      'fetchPermissions',
      'updatePermissions',
      'changeMode',
      'setBidderBlock',
    ]),
    ...mapActions([
      'sincronizarIntencaoRecursoPorSituacao',
      'manifestarOfertaCompraIntencaoRecurso',
    ]),
    addNewMessage(data) {
      this.addMessage(data);
      const scrollToBottom = this.handleScroll(this.chat);
      this.notification(scrollToBottom);
    },
    loadMoreMessages() {
      if (this.total > this.messages.length) {
        this.total = this.messages.length;
      } else {
        this.total += MESSAGES_PER_LOAD;
      }
    },
    goDownChat() {
      this.$nextTick(function scrollChat() {
        const chat = this.$refs.messages;
        chat.scrollTop = chat.scrollHeight + 1500;
      });

      this.quantity = 0;
    },
    async sendMessage() {
      if (this.message !== '') {
        this.loading = true;
        const dados = {
          id: `M${this.messages.length + 1}`,
          tipo: 2,
          usuario: this.bidder
            ? this.userLoggedBidder.alias
            : NON_BIDDER_SENDER_LABELS[this.function] || 'Pregoeiro(a)',
          mensagem: this.message,
        };
        await this.addMessage(dados);
        this.socketResource.sendMessage({
          id: dados.id,
          message: this.message,
        });
        this.goDownChat();
      }
      this.message = '';
      this.loading = false;
    },
    firstLetterUpperCase(message) {
      if (message && message.length === 1) {
        setTimeout(() => {
          this.message = message.charAt(0).toUpperCase();
        }, 1);
      }
    },
    handleScroll(chat) {
      if (chat.scrollHeight - chat.scrollTop === chat.clientHeight) {
        this.scrollBottom = true;
        this.quantity = 0;
        return true;
      }

      this.scrollBottom = false;
      return false;
    },
    notification(scrollToBottom) {
      if (!this.isActive) {
        this.quantity += 1;
        return;
      }

      if (this.isActive) {
        if (scrollToBottom) {
          this.goDownChat();
        } else {
          this.quantity += 1;
        }
      }
    },
    reduceSocialDenomination(socialDenomination) {
      return socialDenomination.split(' ').slice(0, 2).join(' ');
    },
    toggleChat() {
      this.quantity = 0;
      this.$refs.chat.classList.toggle('chat-ativo');
      this.viewParticipants = false;
      this.isActive = !this.isActive;

      this.goDownChat();
      if (this.isActive && this.displayFieldButton) this.$refs.inputMessage.focus();
    },
    async fetchAppealIntention(situacaoAnterior) {
      if (!this.auction.id || !this.bidder) {
        return;
      }
      try {
        await this.sincronizarIntencaoRecursoPorSituacao({
          situacaoAnterior,
          situacaoAtual: this.situation,
          ofertaCompraId: this.auction.id,
        });
      } catch (error) {
        this.$alerta(error.message, 'is-danger');
      }
    },
    async manifestAppealIntention() {
      if (this.hasAppealIntention) {
        return;
      }
      this.manifestAppealLoading = true;
      try {
        await this.manifestarOfertaCompraIntencaoRecurso();
        this.$alerta('Intenção de recurso manifestada com sucesso', 'is-success');
      } catch (error) {
        this.$alerta(error.message, 'is-danger');
      } finally {
        this.manifestAppealLoading = false;
      }
    },
    async changeChatModeTo(mode) {
      if (mode === this.chatMode || this.changingChatMode) {
        return;
      }
      this.changingChatMode = true;
      try {
        await this.changeMode({ auctionId: this.auction.id, mode });
      } catch (error) {
        this.$alerta(error.message, 'is-danger');
      } finally {
        this.changingChatMode = false;
      }
    },
    isBidderBlocked(bidderId) {
      return this.blockedBidderIds.includes(bidderId);
    },
    async toggleBidderBlock(participant) {
      const bidderId = participant.id[0];
      try {
        await this.setBidderBlock({
          auctionId: this.auction.id,
          bidderId,
          blocked: !this.isBidderBlocked(bidderId),
        });
      } catch (error) {
        this.$alerta(error.message, 'is-danger');
      }
    },
  },
  created() {
    this.fetchAppealIntention();
    this.fetchPermissions(this.auction.id);
  },
};
</script>

<style lang="scss">
.chat {
  bottom: 0;
  right: 1.2rem;
  display: flex !important;
  position: fixed;
  width: 45%;
  z-index: 5;
}

.chat-header {
  cursor: pointer;
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.chat-content {
  font-size: 0.96rem;
  height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  -webkit-transition: all 300ms ease;
  -moz-transition: all 300ms ease;
  -o-transition: all 300ms ease;
  transition: all 300ms ease;
}

.chat-content.chat-ativo {
  height: 340px;
}

.chat-counter {
  left: 15px;
  margin: -15px 0 0 -15px;
  position: absolute;
  top: 0;
  width: 35px;
}

.chat-panel {
  width: 100%;
}

.custom-chat-button {
  border-bottom-right-radius: 0 !important;
}

.custom-chat-input > input {
  border-top-left-radius: 0 !important;
}

.down-button {
  position: absolute !important;
  right: 38%;
  bottom: 55px;
  border-color: transparent;
  background-color: #feecf0 !important;
  color: #cc0f35 !important;
}

.down-button:hover {
  border-color: #ffb5c5 !important;
  background-color: #ffd5de !important;
  color: #cc0f35 !important;
}

.down-button-without-message {
  bottom: 25px;
}

.down-button-icon {
  margin-left: 0.4rem !important;
}

.insert-message {
  height: 36px;
}

.chat-restricted-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  font-size: 13px;
  color: #595959;
  background-color: #f5f5f5;
  border-top: 1px solid #dbdbdb;
}

.chat-mode-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  background-color: #f8f9fc;
  border-bottom: 1px solid #dbdbdb;
}

.chat-mode-toolbar-label {
  font-weight: 700;
  font-size: 13px;
  color: #363636;
  white-space: nowrap;
}

.chat-mode-segmented {
  display: flex;
  border: 1px solid #1f4eaa;
  border-radius: 4px;
  overflow: hidden;
}

.chat-mode-segment {
  border: none;
  padding: 6px 12px;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  background-color: white;
  color: #1f4eaa;
}

.chat-mode-segment + .chat-mode-segment {
  border-left: 1px solid #1f4eaa;
}

.chat-mode-segment.is-active {
  background-color: #1f4eaa;
  color: white;
  font-weight: 700;
}

.chat-mode-segment:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.media-content {
  word-break: break-word;
}

.messages {
  max-height: 340px;
  height: 100%;
  overflow-y: auto;
}

.participants-panel {
  margin-bottom: unset !important;
  background-color: #ededed;
  overflow: auto;
}

.participant-content {
  display: flex;
  align-items: center;
  margin-bottom: 0.2rem;
}

.participant-icon {
  vertical-align: middle;
}

.participant-alias {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.participant-block-control {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #b3b3b3;
  border-radius: 3px;
  background-color: white;
  padding: 1px 4px;
  margin-right: 12px;
  cursor: pointer;
}

.participant-block-control.is-blocked {
  background-color: #e6e6e6;
}

.participants-content {
  margin-top: 0.5rem;
  font-size: 0.88rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  min-width: 0;
}

.chat-title {
  flex: 1;
  text-align: center;
}

.chat-header-right {
  min-width: 30px;
  display: flex;
  justify-content: flex-end;
}

.chat-status {
  margin-right: 0.2rem;
}

.split-columns {
  column-count: 2;
}

.load-messages {
  display: block;
}

@media (max-width: 1023px) {
  .chat {
    left: 5%;
    right: 5%;
    width: 90%;
  }

  .chat-content {
    font-size: 0.88rem;
  }

  .chat-panel {
    height: 50%;
    position: relative;
  }

  .participants-panel {
    position: absolute;
    width: 100%;
    height: 36%;
    z-index: 1;
    top: 14%;
    border-radius: 0 0 6px 6px !important;
  }
}

@media (min-width: 1024px) {
  .increase-chat-size {
    width: 56.25% !important;
  }

  .participants-panel {
    width: 25%;
    border-radius: 6px 0 0 0 !important;
  }

  .remove-border-chat-header {
    border-radius: 0 6px 0 0 !important;
  }
}

@media (min-width: 1500px) {
  .chat {
    width: 40%;
  }

  .chat-content.chat-ativo {
    height: 400px;
  }

  .increase-chat-size {
    width: 50% !important;
  }

  .messages {
    max-height: 400px;
  }

  .panel-content {
    height: 455px;
  }
}
</style>
